'use client';

import React, { createContext, useContext } from 'react';

// React context that carries the consumer-supplied base URL (e.g. https://www.srp.com)
// for the standalone <srp-header /> + <srp-footer /> bundle. Only provided inside those
// custom elements — undefined in the regular Next.js app, so the rewrite is a no-op there.
const RedirectBaseUrlContext = createContext<string | undefined>(undefined);

// Provider that exposes the redirect base URL to descendants in the web-component React tree
export const RedirectBaseUrlProvider = ({
  baseUrl,
  children,
}: {
  baseUrl?: string;
  children: React.ReactNode;
}) => {
  return <RedirectBaseUrlContext.Provider value={baseUrl}>{children}</RedirectBaseUrlContext.Provider>;
};

// Read the active base URL (undefined when not inside a web component)
export const useRedirectBaseUrl = (): string | undefined => useContext(RedirectBaseUrlContext);

// Prefix a root-relative href (/about-us) with the base URL when one is supplied.
// Absolute URLs, hashes, mailto:/tel:, and falsy values are returned unchanged.
export const applyRedirectBaseUrl = (href: string | undefined, baseUrl: string | undefined): string | undefined => {
  if (!href || !baseUrl) return href;
  if (!href.startsWith('/')) return href;
  const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${trimmedBase}${href}`;
};

// Context that controls whether root-relative mega menu links are made absolute using
// redirectSearchBaseUrl. Defaults to false — opt-in per consumer via useabsoluteurls attribute.
const UseAbsoluteUrlsContext = createContext<boolean>(false);

// Provider that exposes the absolute-URL flag to all nav link descendants
export const UseAbsoluteUrlsProvider = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) => (
  <UseAbsoluteUrlsContext.Provider value={enabled}>{children}</UseAbsoluteUrlsContext.Provider>
);

// Returns true only when the consuming app has set useabsoluteurls="true" on the web component
export const useAbsoluteUrls = (): boolean => useContext(UseAbsoluteUrlsContext);
